import React, { createContext, useContext, useEffect, useState } from 'react';
import { login as apiLogin, logout as apiLogout, getCurrentUser, isAuthenticated as checkIsAuthenticated } from '@/services/authService';

interface User {
  id: number;
  email: string;
  first_name: string;
  last_name: string;
  organization?: {
    id: string;
    name: string;
    owner: string;
    city: string;
  }
}

interface AuthContextType {
  user: User | null;
  isAuthenticated: boolean;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => void;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType>({
  user: null,
  isAuthenticated: false,
  login: async () => false,
  logout: () => {},
  loading: true
});

export const useAuth = () => useContext(AuthContext);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    // Check if user is already logged in
    const checkAuth = async () => {
      try {
        // Force set the correct token first
        const correctToken = "99b4972f32c2a427c8cc1f7eed3dbe61e0f4bf10cbe5e17e7f471d6bbe2cde52";
        localStorage.setItem("disco_auth_token", correctToken);
        console.log("Set correct token in AuthContext");
        
        // First check if token exists and is valid
        const validSession = checkIsAuthenticated();
        const currentUser = getCurrentUser();
        
        console.log("Auth check on initial load:", { validSession, currentUser });
        
        if (validSession && currentUser) {
          setUser(currentUser);
          setIsAuthenticated(true);
        } else {
          // Clear invalid auth state
          await apiLogout();
          setUser(null);
          setIsAuthenticated(false);
        }
      } catch (error) {
        console.error("Error checking authentication:", error);
        // Clear potentially corrupted auth state
        await apiLogout();
        setUser(null);
        setIsAuthenticated(false);
      } finally {
        // Always set loading to false when done
        setLoading(false);
      }
    };
    
    checkAuth();
  }, []);

  const login = async (username: string, password: string): Promise<boolean> => {
    setLoading(true);
    try {
      const response = await apiLogin({ username, password });
      
      if (response) {
        // Ensure we're setting the user correctly
        if (response.user) {
          setUser(response.user);
        } else {
          // If no user data in response, get from localStorage
          const currentUser = getCurrentUser();
          if (currentUser) {
            setUser(currentUser);
          }
        }
        setIsAuthenticated(true);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Login error in context:", error);
      return false;
    } finally {
      setLoading(false);
    }
  };

  const logout = () => {
    apiLogout().then(() => {
      setUser(null);
      setIsAuthenticated(false);
    });
  };

  return (
    <AuthContext.Provider value={{
      user,
      isAuthenticated,
      login,
      logout,
      loading
    }}>
      {children}
    </AuthContext.Provider>
  );
};
